export const initialState = {
    isDarkMode: false,
    mainLayout: 'app',
    theme: 'light',
    menu: 'default',
    layout: 'full',
    rtlClass: 'ltr',
    animation: 'animate__fadeInRight',
    navbar: 'navbar-sticky',
    locale: 'en',
    sidebar: false,
    languageList: [
        { code: 'zh', name: 'Chinese' },
        { code: 'da', name: 'Danish' },
        { code: 'en', name: 'English' },
        { code: 'fr', name: 'French' },
        { code: 'de', name: 'German' },
        { code: 'el', name: 'Greek' },
        { code: 'hu', name: 'Hungarian' },
        { code: 'it', name: 'Italian' },
        { code: 'ja', name: 'Japanese' },
        { code: 'pl', name: 'Polish' },
        { code: 'pt', name: 'Portuguese' },
        { code: 'ru', name: 'Russian' },
        { code: 'es', name: 'Spanish' },
        { code: 'sv', name: 'Swedish' },
        { code: 'tr', name: 'Turkish' },
        { code: 'ae', name: 'Arabic' },
    ],
    isShowMainLoader: true,
    semidark: false,
};

type IndexState = typeof initialState;

function persistToLocalStorage(key: string, value: unknown): void {
    if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
        localStorage.setItem(key, value as string);
    }
}

function setHtmlDir(dir: string): void {
    if (typeof document !== 'undefined') {
        document.querySelector('html')?.setAttribute('dir', dir || 'ltr');
    }
}

function applyDarkModeClass(isDarkMode: boolean): void {
    if (typeof document === 'undefined') {
        return;
    }
    if (isDarkMode) {
        document.querySelector('body')?.classList.add('dark');
    } else {
        document.querySelector('body')?.classList.remove('dark');
    }
}

function resolveDarkMode(theme: string, currentIsDarkMode: boolean): boolean {
    if (theme == 'light') {
        return false;
    }
    if (theme == 'dark') {
        return true;
    }
    return currentIsDarkMode || false;
}

function handleToggleDirection(state: IndexState, payload: string): IndexState {
    persistToLocalStorage('direction', payload);
    setHtmlDir(payload);
    return { ...state, ...{ direction: payload } };
}

function handleToggleMainLoader(state: IndexState, payload: boolean): IndexState {
    return { ...state, ...{ isShowMainLoader: payload } };
}

function handleSetMainLayout(state: IndexState, payload: string): IndexState {
    return { ...state, ...{ mainLayout: payload } };
}

function handleToggleTheme(state: IndexState, payload: string): IndexState {
    const theme = payload || state.theme;
    persistToLocalStorage('theme', theme);
    const isDarkMode = resolveDarkMode(theme, state.isDarkMode);
    applyDarkModeClass(isDarkMode);
    return { ...state, ...{ theme, isDarkMode } };
}

function handleToggleMenu(state: IndexState, payload: string): IndexState {
    const menu = payload || state.menu;
    persistToLocalStorage('menu', menu);
    return { ...state, ...{ sidebar: false, menu } };
}

function handleToggleLayout(state: IndexState, payload: string): IndexState {
    const layout = payload || state.layout;
    persistToLocalStorage('layout', layout);
    return { ...state, ...{ layout } };
}

function handleToggleRTL(state: IndexState, payload: string): IndexState {
    const rtlClass = payload || state.rtlClass;
    persistToLocalStorage('rtlClass', rtlClass);
    setHtmlDir(rtlClass);
    return { ...state, ...{ rtlClass } };
}

function handleToggleAnimation(state: IndexState, payload: string): IndexState {
    const animation = payload?.trim();
    persistToLocalStorage('animation', animation);
    if (animation && typeof document !== 'undefined') {
        const eleanimation: Element | null = document.querySelector('.animation');
        eleanimation?.classList.add('animate__animated');
        eleanimation?.classList.add(animation);
    }
    return { ...state, ...{ animation } };
}

function handleToggleNavbar(state: IndexState, payload: string): IndexState {
    const navbar = payload || state.navbar;
    persistToLocalStorage('navbar', navbar);
    return { ...state, ...{ navbar } };
}

function handleToggleSemidark(state: IndexState, payload: boolean): IndexState {
    const semidark = payload || false;
    persistToLocalStorage('semidark', semidark);
    return { ...state, ...{ semidark } };
}

function handleToggleLocale(state: IndexState, payload: string): IndexState {
    const locale = payload || state.locale;
    persistToLocalStorage('i18n_locale', locale);
    return { ...state, ...{ locale } };
}

function handleToggleSidebar(state: IndexState): IndexState {
    return { ...state, ...{ sidebar: !state.sidebar } };
}

const indexActionHandlers: Record<string, (state: IndexState, payload?: any) => IndexState> = {
    toggleDirection: handleToggleDirection,
    toggleMainLoader: handleToggleMainLoader,
    setMainLayout: handleSetMainLayout,
    toggleTheme: handleToggleTheme,
    toggleMenu: handleToggleMenu,
    toggleLayout: handleToggleLayout,
    toggleRTL: handleToggleRTL,
    toggleAnimation: handleToggleAnimation,
    toggleNavbar: handleToggleNavbar,
    toggleSemidark: handleToggleSemidark,
    toggleLocale: handleToggleLocale,
    toggleSidebar: (state) => handleToggleSidebar(state),
};

export function indexReducer(state = initialState, action: any) {
    const handler = indexActionHandlers[action.type];
    return handler ? handler(state, action.payload) : state;
}
